import { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { FaPaperPlane, FaCheckCircle } from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import laporanService from '../services/laporanService';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const ChangeView = ({ center, zoom }) => {
    const map = useMap();
    map.setView(center, zoom);
    return null;
};

const FormLaporan = ({ onLaporanCreated }) => {
    const [kategoriLaporan, setKategoriLaporan] = useState('');
    const [deskripsiLaporan, setDeskripsiLaporan] = useState('');
    const [dokumen, setDokumen] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const [namaPelapor, setNamaPelapor] = useState('');
    const [email, setEmail] = useState('');
    const [noTelp, setNoTelp] = useState('');
    const [alamat, setAlamat] = useState('');
    const [ditujukanKepada, setDitujukanKepada] = useState('');
    const [terlapor, setTerlapor] = useState('');

    const [position, setPosition] = useState([-6.175392, 106.827153]);
    const [detailLokasiManual, setDetailLokasiManual] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchingMap, setSearchingMap] = useState(false);

    // Fungsi otomatis membuat kode unik UUID sesi pelapor anonim
    const getOrCreateSessionId = () => {
        let id = localStorage.getItem("sipuma_session_id");
        if (!id) {
            id = 'session-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
            localStorage.setItem("sipuma_session_id", id);
        }
        return id;
    };

    function LocationMarker() {
        useMapEvents({
            click(e) {
                setPosition([e.latlng.lat, e.latlng.lng]);
            },
        });
        return position === null ? null : <Marker position={position} />;
    }

    const handleSearchLocation = async () => {
        if (!searchQuery) return;
        setSearchingMap(true);
        try {
            const response = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`);
            if (response.data && response.data.length > 0) {
                const { lat, lon } = response.data[0];
                setPosition([parseFloat(lat), parseFloat(lon)]);
            } else {
                alert("Lokasi tidak ditemukan.");
            }
        } catch (err) {
            console.error(err);
        } finally {
            setSearchingMap(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');
        setLoading(true);

        if (!kategoriLaporan || !deskripsiLaporan) {
            setError("Kategori dan Deskripsi laporan wajib diisi!");
            setLoading(false);
            return;
        }

        const sessionId = getOrCreateSessionId();
        const formData = new FormData();
        formData.append('kategoriLaporan', kategoriLaporan);
        formData.append('deskripsiLaporan', deskripsiLaporan);
        formData.append('namaPelapor', namaPelapor || "Anonim");
        formData.append('email', email);
        formData.append('noTelp', noTelp);
        formData.append('alamat', alamat);
        formData.append('ditujukanKepada', ditujukanKepada);
        formData.append('terlapor', terlapor);
        formData.append('lokasiKejadian', detailLokasiManual);
        formData.append('koordinatLat', position[0]);
        formData.append('koordinatLng', position[1]);
        formData.append('sessionId', sessionId);

        if (dokumen) {
            formData.append('dokumen', dokumen);
        }

        try {
            const res = await laporanService.createLaporan(formData);
            setMessage(`Laporan berhasil dikirim! Simpan Nomor Tiket Anda: ${res.data.nomorTiket}`);
            setKategoriLaporan('');
            setDeskripsiLaporan('');
            setDokumen(null);
            setNamaPelapor('');
            setEmail('');
            setNoTelp('');
            setAlamat('');
            setDitujukanKepada('');
            setTerlapor('');
            setDetailLokasiManual('');
            if (onLaporanCreated) onLaporanCreated();
        } catch (err) {
            setError(err.response?.data || "Gagal mengirimkan laporan ke server.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="form-laporan-container">
            <Card className="shadow-sm border-0 rounded-3">
                <Card.Header className="bg-primary text-white py-3">
                    <h5 className="mb-0 fw-bold">Formulir Pengaduan Masyarakat</h5>
                </Card.Header>
                <Card.Body className="p-4">
                    {message && <Alert variant="success" className="d-flex align-items-center"><FaCheckCircle className="me-2"/>{message}</Alert>}
                    {error && <Alert variant="danger">{error}</Alert>}
                    
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Nama Pelapor</Form.Label>
                                    <Form.Control type="text" placeholder="Masukan nama lengkap anda" value={namaPelapor} onChange={(e) => setNamaPelapor(e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Email</Form.Label>
                                    <Form.Control type="email" placeholder="Contoh: pelapor@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Nomor Telepon / WA</Form.Label>
                                    <Form.Control type="text" placeholder="Masukkan nomor telepon aktif" value={noTelp} onChange={(e) => setNoTelp(e.target.value)} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Alamat Domisili</Form.Label>
                                    <Form.Control type="text" placeholder="Masukkan alamat lengkap Anda" value={alamat} onChange={(e) => setAlamat(e.target.value)} />
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr/>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Kategori Pelanggaran</Form.Label>
                                    <Form.Select value={kategoriLaporan} onChange={(e) => setKategoriLaporan(e.target.value)}>
                                        <option value="">-- Pilih Kategori --</option>
                                        <option value="Pelanggaran Petugas">Pelanggaran Disiplin Petugas</option>
                                        <option value="Penyelundupan">Dugaan Penyelundupan / Ilegal</option>
                                        <option value="Suap / Pungli">Pungutan Liar (Pungli) / Gratifikasi</option>
                                        <option value="Layanan Operasional">Keluhan Layanan Kepabeanan</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Ditujukan Kepada</Form.Label>
                                    <Form.Control type="text" placeholder="Contoh: PT ABC" value={ditujukanKepada} onChange={(e) => setDitujukanKepada(e.target.value)} />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Nama Terlapor</Form.Label>
                            <Form.Control type="text" placeholder="Masukan nama personal / divisi terlapor" value={terlapor} onChange={(e) => setTerlapor(e.target.value)} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold">Kronologi & Deskripsi Lengkap Kejadian</Form.Label>
                            <Form.Control as="textarea" rows={4} placeholder="Tuliskan secara lengkap detail kejadian perkara..." value={deskripsiLaporan} onChange={(e) => setDeskripsiLaporan(e.target.value)} />
                        </Form.Group>

                        <div className="mb-3 p-3 bg-light rounded-3 border">
                            <Form.Label className="fw-bold text-primary">Lokasi Peta Kejadian</Form.Label>
                            <Row className="mb-2">
                                <Col md={9}>
                                    <Form.Control type="text" placeholder="Cari nama jalan / wilayah kejadian..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                                </Col>
                                <Col md={3}>
                                    <Button variant="secondary" className="w-100" onClick={handleSearchLocation} disabled={searchingMap}>
                                        {searchingMap ? 'Mencari...' : 'Cari Lokasi'}
                                    </Button>
                                </Col>
                            </Row>
                            <div style={{ height: '250px', width: '100%' }} className="rounded-2 overflow-hidden mb-2">
                                <MapContainer center={position} zoom={13} style={{ height: '100%', width: '100%' }}>
                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                    <LocationMarker />
                                    <ChangeView center={position} zoom={13} />
                                </MapContainer>
                            </div>
                            <Form.Group>
                                <Form.Label className="small text-muted fw-semibold">Patokan Lokasi Manual</Form.Label>
                                <Form.Control as="textarea" rows={2} placeholder="Contoh: Depan terminal kontainer, samping pos jaga..." value={detailLokasiManual} onChange={(e) => setDetailLokasiManual(e.target.value)} />
                            </Form.Group>
                        </div>

                        <Form.Group className="mb-4">
                            <Form.Label className="fw-semibold">Upload Foto Bukti Dokumen (Opsional)</Form.Label>
                            <Form.Control type="file" onChange={(e) => setDokumen(e.target.files[0])} />
                        </Form.Group>

                        <div className="d-grid">
                            <Button variant="primary" type="submit" size="lg" disabled={loading}>
                                {loading ? 'Mengirim laporan...' : <><FaPaperPlane className="me-2" /> Kirim Pengaduan Aman</>}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default FormLaporan;